import React from "react";
import {
  BadgeCheck,
  BriefcaseBusiness,
  Check,
  MapPinned,
  Upload as UploadIcon,
  UserRoundPlus,
} from "lucide-react";

import { STEP_META } from "./config";

const STEP_ICONS = {
  1: UserRoundPlus,
  2: MapPinned,
  3: BriefcaseBusiness,
  4: BadgeCheck,
};

export function StepRail({ step, completed, onStepSelect }) {
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {STEP_META.map((item) => {
        const Icon = STEP_ICONS[item.id];
        const active = step === item.id;
        const isDone = completed[item.id];

        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onStepSelect?.(item.id)}
            aria-current={active ? "step" : undefined}
            className={`min-w-0 rounded-[22px] border px-2 py-3 transition ${
              active
                ? "border-primary bg-primary text-primaryForeground shadow-[0_16px_28px_rgba(59,90,63,0.18)]"
                : isDone
                  ? "border-primary/15 bg-primary/5 text-primary"
                  : "border-border bg-white text-mutedForeground"
            } text-left hover:border-primary/35`}
          >
            <div className="flex h-full flex-col items-center gap-2 text-center">
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  active ? "bg-white/15" : isDone ? "bg-primary/10" : "bg-secondary"
                }`}
              >
                {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </div>
              <div className="w-full min-w-0">
                <p className="whitespace-nowrap text-[11px] uppercase tracking-[0.18em] opacity-80">Paso {item.id}</p>
                <p className="mt-1 min-h-[2.5rem] break-words text-sm font-semibold leading-5">{item.label}</p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="mb-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-primary/70">{eyebrow}</p>
      <h2 className="mt-2 font-heading text-3xl font-semibold text-primary">{title}</h2>
      <p className="mt-2 max-w-3xl text-sm leading-6 text-mutedForeground">{description}</p>
    </div>
  );
}

export function Field({ label, error, hint, required = false, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-primary">
        {label}
        {required ? <span className="ml-1 text-[#8A3D22]">*</span> : null}
      </span>
      {children}
      {error ? <span className="mt-2 block text-xs font-medium text-[#8A3D22]">{error}</span> : null}
      {!error && hint ? <span className="mt-2 block text-xs text-mutedForeground">{hint}</span> : null}
    </label>
  );
}

export function ChoiceGrid({ title, description, options, value, onChange, columnsClass = "md:grid-cols-3" }) {
  return (
    <div className="rounded-[24px] border border-primary/10 bg-[#F9FBF5] p-5">
      <h3 className="font-heading text-xl font-semibold text-primary">{title}</h3>
      {description ? <p className="mt-2 text-sm leading-6 text-mutedForeground">{description}</p> : null}
      <div className={`mt-4 grid gap-3 ${columnsClass}`}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`rounded-2xl border px-4 py-4 text-left transition ${
              value === option.value
                ? "border-primary bg-primary text-primaryForeground"
                : "border-border bg-white text-textMain hover:border-primary/35"
            }`}
          >
            <p className="text-sm font-semibold">{option.label}</p>
            {option.description ? (
              <p className={`mt-2 text-xs leading-5 ${value === option.value ? "text-primaryForeground/85" : "text-mutedForeground"}`}>
                {option.description}
              </p>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

export function MultiSelectGrid({ options, value = [], onToggle }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {options.map((option) => {
        const active = value.includes(option);
        return (
          <button
            key={option}
            type="button"
            onClick={() => onToggle(option)}
            className={`rounded-2xl border px-4 py-3 text-left text-sm font-medium transition ${
              active ? "border-primary bg-primary text-primaryForeground" : "border-border bg-white text-textMain hover:border-primary/35"
            }`}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

export function StatusChip({ children, tone = "default" }) {
  const classes = {
    default: "border-primary/12 bg-primary/5 text-primary",
    warning: "border-[#D8B774]/30 bg-[#FFF8E7] text-[#8A6B22]",
    positive: "border-[#5FA36A]/25 bg-[#EFF8EF] text-[#2F6B3A]",
  };

  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${classes[tone]}`}
    >
      {children}
    </span>
  );
}

export function UploadCard({
  title,
  subtitle,
  icon: Icon = UploadIcon,
  onChange,
  accept = "image/*",
  multiple = false,
}) {
  return (
    <div className="rounded-[22px] border border-dashed border-primary/20 bg-white px-4 py-4">
      <label className="flex cursor-pointer items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-primary">{title}</p>
            <p className="text-xs text-mutedForeground">{subtitle}</p>
          </div>
        </div>
        <span className="rounded-2xl border border-primary/20 px-4 py-2 text-sm font-semibold text-primary">
          Seleccionar
        </span>
        <input type="file" multiple={multiple} accept={accept} className="hidden" onChange={onChange} />
      </label>
    </div>
  );
}

export function SummaryCard({ icon: Icon, label, value, description }) {
  return (
    <div className="rounded-[24px] border border-primary/10 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70">{label}</p>
          <p className="mt-3 font-heading text-2xl font-semibold text-primary">{value}</p>
          <p className="mt-2 text-sm leading-6 text-mutedForeground">{description}</p>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}
